import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NetworkErrorDisplay } from "../atoms/NetworkErrorDisplay";
import { useAuth } from "../../contexts/AuthContext";
import { backendApiService } from "../../lib/services/backendApiService";
import { ProductType } from "../../lib/utils/stripeProductMapping";

// Academic email validation helper
const isAcademicEmail = (email: string): boolean => {
  if (!email) return false;
  const emailLower = email.toLowerCase();
  const academicDomains = [
    ".edu",
    ".edu.au",
    ".edu.cn",
    ".ac.uk",
    ".ac.nz",
    ".ac.za",
    ".ac.jp",
    ".ac.in",
    ".edu.sg",
    ".edu.hk",
  ];
  return academicDomains.some((domain) => emailLower.endsWith(domain));
};

const PRODUCT_OPTIONS = [
  {
    id: "monthly_academic" as ProductType,
    name: "Academic",
    description: "Perfect for students and educators",
    frequency: "monthly",
    tier: "academic",
  },
  {
    id: "yearly_academic" as ProductType,
    name: "Academic",
    description: "Perfect for students and educators",
    frequency: "yearly",
    tier: "academic",
  },
  {
    id: "monthly_professional" as ProductType,
    name: "Professional",
    description: "For professional use and businesses",
    frequency: "monthly",
    tier: "professional",
  },
  {
    id: "yearly_professional" as ProductType,
    name: "Professional",
    description: "For professional use and businesses",
    frequency: "yearly",
    tier: "professional",
  },
];

export const SubscriptionTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { user, subscriptionError, refreshSubscription } = useAuth();

  // Check for successful checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("success")) {
      navigate("/trees", {
        state: {
          verifySubscription: true,
        },
      });
    }

    if (params.get("cancel") || params.get("error")) {
      setError("Payment processing was cancelled or failed. Please try again.");
    }
  }, [location.search, navigate]);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      setLoading(true);
      setError(null);
      try {
        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Request timeout")), 10000),
        );

        const data = (await Promise.race([
          backendApiService.getSubscriptionStatus(),
          timeoutPromise,
        ])) as any;

        if (!mounted) return;
        setSubscription(data);

        const storedProduct = localStorage.getItem("stripe_product_type");
        if (
          storedProduct &&
          (!data || data.status === "none") &&
          user?.emailVerified
        ) {
          let finalProduct = storedProduct;

          // Validate academic email for academic plans before auto-checkout
          const isAcademicPlan =
            storedProduct === "monthly_academic" ||
            storedProduct === "yearly_academic";

          if (isAcademicPlan && !isAcademicEmail(user?.email || "")) {
            // Switch to professional at the same billing frequency
            const frequency = storedProduct.includes("monthly")
              ? "monthly"
              : "yearly";
            finalProduct = `${frequency}_professional`;
            console.log(
              `Switching from ${storedProduct} to ${finalProduct} - academic email required`,
            );
          }

          localStorage.removeItem("stripe_product_type");

          setCheckoutLoading(true);
          try {
            const session = await backendApiService.createSubscriptionCheckout(
              finalProduct as ProductType,
            );
            if (session?.url) {
              window.location.href = session.url;
              return;
            }
          } catch (err: any) {
            console.error("Auto-checkout failed:", err);
            setError("Failed to start checkout. Please select a plan below.");
          } finally {
            if (mounted) setCheckoutLoading(false);
          }
        }
      } catch (err: any) {
        console.error("Failed to fetch subscription status", err);
        if (!mounted) return;
        setError(
          "Failed to fetch subscription information. Please try refreshing the page.",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchStatus();
    return () => {
      mounted = false;
    };
  }, [user?.emailVerified]);

  const handleManage = async () => {
    try {
      const res = await backendApiService.createPortalSession();

      if (res?.requires_checkout && res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }

      if (res?.url) {
        window.location.href = res.url;
        return;
      }

      setError("Failed to open portal - no URL returned");
    } catch (err: any) {
      console.error("createPortalSession failed", err);
      setError(`Failed to open portal: ${err.message || "Unknown error"}`);
    }
  };

  const handleSelectProduct = async (
    productId: ProductType,
    coupon?: string,
  ) => {
    // Check for academic license validation
    const isAcademicPlan =
      productId === "monthly_academic" || productId === "yearly_academic";

    if (isAcademicPlan && !isAcademicEmail(user?.email || "")) {
      setError(
        "Academic licenses require an academic email address (.edu, .ac.uk, .edu.au, etc.). " +
          "Please update your email in the Profile tab or select a Professional plan.",
      );
      return;
    }

    if (!user?.emailVerified) {
      setError("Please verify your email before subscribing");
      return;
    }

    setCheckoutLoading(true);
    setError(null);

    try {
      const session = await backendApiService.createSubscriptionCheckout(
        productId,
        coupon ? { coupon } : undefined,
      );
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      setError("Failed to create checkout session");
    } catch (err: any) {
      console.error("Failed to create checkout session:", err);
      setError(
        err.message || "Failed to create checkout session. Please try again.",
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const navState: any = location.state || {};
  const stripeError = navState?.stripeError;

  // Note: Email verification is now handled by SettingsPage modal
  // Users with unverified emails will see a non-dismissible modal on the settings page

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (subscriptionError) {
    return (
      <NetworkErrorDisplay
        errorMessage={subscriptionError}
        onRetry={() => refreshSubscription()}
      />
    );
  }

  if (error && !subscription) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Card sx={{ width: "100%", maxWidth: 600 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // No subscription - show product selection
  if (!subscription || subscription.status === "none") {
    const userHasAcademicEmail = isAcademicEmail(user?.email || "");

    return (
      <Box sx={{ px: 2 }}>
        <Box sx={{ width: "100%", maxWidth: 900, mx: "auto" }}>
          <Typography
            variant="h4"
            gutterBottom
            textAlign="center"
            sx={{ mb: 4, color: "primary.main" }}
          >
            Choose Your Plan
          </Typography>

          {checkoutLoading && (
            <Box display="flex" justifyContent="center" mb={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!userHasAcademicEmail && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Academic plans require an academic email address (.edu, .ac.uk,
              etc.). Update your email in the Profile tab to access academic
              pricing.
            </Alert>
          )}

          <Grid container spacing={3}>
            {PRODUCT_OPTIONS.map((product) => {
              const isAcademicProduct = product.tier === "academic";
              const isDisabled =
                checkoutLoading ||
                !user?.emailVerified ||
                (isAcademicProduct && !userHasAcademicEmail);

              return (
                <Grid size={{ xs: 12, sm: 6 }} key={product.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      border: "1px solid",
                      borderColor: isDisabled ? "grey.300" : "primary.light",
                      opacity: isDisabled ? 0.6 : 1,
                      "&:hover": !isDisabled
                        ? {
                            transform: "translateY(-4px)",
                            boxShadow: 6,
                            borderColor: "primary.main",
                          }
                        : {},
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            color: isDisabled
                              ? "text.disabled"
                              : "primary.dark",
                          }}
                        >
                          {product.name}
                        </Typography>
                        <Chip
                          label={
                            product.frequency === "yearly"
                              ? "Yearly"
                              : "Monthly"
                          }
                          size="small"
                          color="primary"
                          variant={
                            product.frequency === "yearly"
                              ? "filled"
                              : "outlined"
                          }
                          disabled={isDisabled}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color={isDisabled ? "text.disabled" : "text.secondary"}
                        sx={{ mb: 3 }}
                      >
                        {product.description}
                      </Typography>
                      {isAcademicProduct && !userHasAcademicEmail && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ mb: 2, display: "block" }}
                        >
                          Requires academic email
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleSelectProduct(product.id)}
                        disabled={isDisabled}
                      >
                        Select Plan
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    );
  }

  // User has a subscription
  const currentProduct = PRODUCT_OPTIONS.find(
    (p) => p.id === subscription.tier,
  );

  return (
    <Box sx={{ px: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ color: "primary.main" }}
            >
              Subscription Management
            </Typography>

            {/* User Information */}
            {user && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {user.name || "Not provided"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {user.email}
                </Typography>
              </Box>
            )}

            {/* Current Plan */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Current Plan
              </Typography>
              <Box sx={{ mb: 0.5 }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: "primary.dark" }}
                >
                  {currentProduct?.name || subscription.tier} (
                  {currentProduct?.frequency === "yearly"
                    ? "Yearly"
                    : "Monthly"}
                  )
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {subscription.current_period_end &&
                  `Renews ${new Date(
                    subscription.current_period_end,
                  ).toLocaleDateString()}`}
              </Typography>
              {subscription.cancel_at_period_end && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  Cancels at period end
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleManage}
              disabled={!user?.emailVerified && !!user}
            >
              Manage Subscription
            </Button>

            {stripeError && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                {stripeError}
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
